import { ChangeDetectionStrategy, Component, computed, inject } from "@angular/core";
import { EventFormStore } from "../../../standalone/event-form/event.form.store";
import { EventFormObjectOccurrencesTableComponent } from "../../../standalone/event-form/object-occurrences/event.form.object.occurrences.table.component";
import { EventForm } from "../../../standalone/event-form";
import { S25Bool } from "../../../util/core/s25.bool";
import { Layout } from "../../s25-object-search/s25.object.search.util";
import { closeModalThenAnnounce } from "../modal.close.a11y.util";
import { ModalComponent } from "../modal.component";
import { ModalHeaderComponent } from "../modal.header.component";
import type { ModalData } from "../modal.service";

export interface EventFormObjectOccurrencesTableModalData {
    item: { itemName?: string };
    objOccurrences: EventForm.ObjectOccurrence[];
    /** Invoked whenever the modal closes (footer or header) so AngJS can refresh details */
    onClosed?: () => void | Promise<unknown>;
}

@Component({
    standalone: true,
    imports: [ModalHeaderComponent, EventFormObjectOccurrencesTableComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <s25-modal-header [data]="headerParams()" />
        <div class="modal-body">
            @if (params(); as modalParams) {
                <s25-ng-event-form-object-occurrences-table
                    [objOccurrences]="modalParams.objOccurrences"
                    [layouts]="itemLayouts()"
                    [alwaysShared]="itemAlwaysShared()"
                />
            }
        </div>
        <div class="modal-footer">
            <div class="aw-button-group">
                <button type="button" class="aw-button aw-button--outline" (click)="cancel()">Cancel</button>
                <button type="button" class="aw-button aw-button--primary" (click)="close()">Apply</button>
            </div>
        </div>
    `,
})
export class EventFormObjectOccurrencesTableModal extends ModalComponent<EventFormObjectOccurrencesTableModalData> {
    static readonly size = "lg" as const;
    static readonly title = "Occurrences";
    static readonly asDialog = true;
    static readonly windowClass = "event-form-object-occurrences";

    private readonly eventForm = inject(EventFormStore);

    readonly itemLayouts = computed(() => this.params()?.objOccurrences?.[0]?.item?.layouts ?? []);
    readonly itemAlwaysShared = computed(() => S25Bool.toBool(this.params()?.objOccurrences?.[0]?.item?.always_shared));

    /** Header X discards edits (same as Cancel); footer Close keeps edits. */
    readonly headerParams = computed(() => {
        const modalParams = this.params();
        if (!modalParams) return modalParams;
        return {
            ...modalParams,
            closeModal: () => this.cancel(),
        };
    });

    private closeWrapped = false;
    private closingViaCancel = false;
    private occurrenceEditsByUuid = new Map<number, EventForm.ObjectOccurrenceEdits>();

    /**
     * ModalService assigns `data` after `ngbModal.open()` finishes the view lifecycle.
     * Wrap close here so header X and footer Close both run onClosed inside AngJS $timeout.
     */
    override set data(value: EventFormObjectOccurrencesTableModalData & ModalData) {
        super.data = value;
        this.snapshotOccurrenceEdits(value.objOccurrences);
        this.wrapCloseModal(value);
    }

    override get data(): EventFormObjectOccurrencesTableModalData & ModalData {
        return super.data;
    }

    close(): void {
        this.params()?.closeModal();
    }

    cancel(): void {
        this.closingViaCancel = true;
        this.restoreOccurrenceEdits();
        this.params()?.closeModal();
    }

    private snapshotOccurrenceEdits(occurrences: EventForm.ObjectOccurrence[] | undefined): void {
        this.occurrenceEditsByUuid = new Map(
            (occurrences ?? []).map((occurrence) => [occurrence.occ.uuid, this.captureOccurrenceEdits(occurrence)]),
        );
    }

    private restoreOccurrenceEdits(): void {
        const modalParams = this.params();
        if (!modalParams) return;

        const occurrences = modalParams.objOccurrences ?? [];
        const formModel = this.eventForm.model();
        const changedLayouts: EventForm.ObjectOccurrence[] = [];
        let quantityChanged = false;
        let availabilityChanged = false;

        for (const occurrence of occurrences) {
            const edits = this.occurrenceEditsByUuid.get(occurrence.occ.uuid);
            if (!edits) continue;
            if (!Layout.isSame(occurrence.layout, edits.layout)) {
                if (occurrence.layout) formModel.undoLayoutAdditionalTime?.(occurrence.layout);
                changedLayouts.push(occurrence);
            }
            if (occurrence.resourceQuantity !== edits.resourceQuantity) quantityChanged = true;
            if (occurrence.isIncluded !== edits.isIncluded || occurrence.spaceShare !== edits.spaceShare) {
                availabilityChanged = true;
            }
        }

        for (const occurrence of occurrences) {
            const edits = this.occurrenceEditsByUuid.get(occurrence.occ.uuid);
            if (edits) this.applyOccurrenceEdits(occurrence, edits);
        }

        for (const occurrence of changedLayouts) {
            if (occurrence.layout) formModel.updateSetupTakedownFromLayout?.(occurrence.layout);
        }
        if (changedLayouts.length) {
            formModel.runRules?.();
            formModel.$broadcast?.(EventForm.BroadcastEvent.ObjectDetailsRefreshOccBasedData, occurrences[0]?.item);
        }
        this.eventForm.bump();

        const item = occurrences[0]?.item;
        const occs = occurrences.map((occurrence) => occurrence.occ);
        if (item && (availabilityChanged || quantityChanged || changedLayouts.length)) {
            void formModel.refreshObjOccAvailability?.(item, occs);
        }
        if (item && quantityChanged) {
            void formModel.refreshObjOccPending?.(item, occs);
        }
    }

    private wrapCloseModal(modalParams: EventFormObjectOccurrencesTableModalData & ModalData): void {
        if (this.closeWrapped || !modalParams.closeModal) {
            return;
        }
        this.closeWrapped = true;
        const originalCloseModal = modalParams.closeModal.bind(modalParams);
        const onClosed = modalParams.onClosed;
        modalParams.closeModal = (result?: unknown) => {
            const announcement = this.closeAnnouncementMessage();
            const finishClose = () => {
                try {
                    onClosed?.();
                } finally {
                    this.closingViaCancel = false;
                    originalCloseModal(result);
                }
            };
            closeModalThenAnnounce(announcement, finishClose);
        };
    }

    private closeAnnouncementMessage(): string {
        const modalParams = this.params();
        const occurrences = modalParams?.objOccurrences ?? [];
        const hasEdits = occurrences.some((occurrence) => {
            const edits = this.occurrenceEditsByUuid.get(occurrence.occ.uuid);
            if (!edits) return false;
            return JSON.stringify(this.captureOccurrenceEdits(occurrence)) !== JSON.stringify(edits);
        });
        if (!this.closingViaCancel && hasEdits) {
            return "Changes applied Modal close";
        }
        return "Modal close";
    }

    private captureOccurrenceEdits(occurrence: EventForm.ObjectOccurrence): EventForm.ObjectOccurrenceEdits {
        return {
            isIncluded: occurrence.isIncluded,
            spaceShare: occurrence.spaceShare,
            layout: occurrence.layout,
            layoutImageId: occurrence.layoutImageId,
            showLayoutPreview: occurrence.showLayoutPreview,
            spaceInstructions: occurrence.spaceInstructions,
            resourceInstructions: occurrence.resourceInstructions,
            spaceAttendance: occurrence.spaceAttendance,
            resourceQuantity: occurrence.resourceQuantity,
            prevResourceQuantity: occurrence.prevResourceQuantity,
        };
    }

    private applyOccurrenceEdits(occurrence: EventForm.ObjectOccurrence, edits: EventForm.ObjectOccurrenceEdits): void {
        occurrence.isIncluded = edits.isIncluded;
        occurrence.spaceShare = edits.spaceShare;
        occurrence.layout = edits.layout;
        occurrence.layoutImageId = edits.layoutImageId;
        occurrence.showLayoutPreview = edits.showLayoutPreview;
        occurrence.spaceInstructions = edits.spaceInstructions;
        occurrence.resourceInstructions = edits.resourceInstructions;
        occurrence.spaceAttendance = edits.spaceAttendance;
        occurrence.resourceQuantity = edits.resourceQuantity;
        occurrence.prevResourceQuantity = edits.prevResourceQuantity;
    }
}
